import {
  hasConversationsLicense,
  hasLicense,
  teamHasDialler,
  uuidV4,
  yesNoConfirm, showToast, addWebsocketListener } from "@sourcewhale/core";
import {
  addSpinner,
  createDurationInterval,
  EU_DIALLER_COUNTRIES,
  getStartDate,
  getUser,
  removeSpinner,
  timestampToDuration,
  userDiallerCountries,
  wrappedGet,
  wrappedPost,
  wrapTooltip,
} from "../general/utils";
import {
  createEditor,
  tinymceOnBeforeUnload,
} from "../general/elements/tinymce.js";

let timeSettingsAreDirty = false;
let contentSettingsAreDirty = false;
let outreachSettingsAreDirty = false;
let emailSettingsAreDirty = false;
let inboxAgentSettingsAreDirty = false;
let displayCustomHolidays;
let customHolidays = [];

const aliasSelector = $("#aliasSelector");

const userHasWhatsapp = () =>
  getUser().kind !== "viewer" && getUser().extensionSmsEnabled;

const refreshLimitInput = () => {
  const input = $("#sendLimitInput");
  const val = input.val();

  input.removeClass("warning-range danger-range");
  $("#sendLimitOutput").show().val(val);

  let tooltip;
  if (val <= 200) tooltip = "This is a generally safe number";
  else if (val <= 500) {
    tooltip = "This level risks going into spam";
    input.addClass("warning-range");
  } else {
    tooltip = "Only choose this level if you have high reply rates";
    input.addClass("danger-range");
  }
  input.tooltip("dispose").tooltip({
    title: tooltip,
    placement: "bottom",
    trigger: "manual",
  });
};

const refreshGapInput = () => {
  const input = $("#minGapInput");
  const val = input.val();

  input.removeClass("warning-range danger-range");
  $("#minGapOutput").show().val(`${val}s`);

  let tooltip;
  if (val >= 60) tooltip = "This is a generally safe number";
  else if (val >= 30) {
    tooltip = "This level risks going into spam";
    input.addClass("warning-range");
  } else {
    tooltip = "Only choose this level if you have high reply rates";
    input.addClass("danger-range");
  }
  input.tooltip("dispose").tooltip({
    title: tooltip,
    placement: "bottom",
    trigger: "manual",
  });
};

// DIALLER INBOUND CALL ROUTING //

const showInboundCallDevices = (device) => {
  const val = device || "extension";
  const selector = $("#incoming-call-device-selector");
  selector.val(val);

  selector.on("change", () => {
    wrappedPost({
      url: "/api/users/save",
      data: JSON.stringify({ updates: { mobileAppDevice: selector.val() } }),
      success: () => {
        showToast({
          title: "Incoming call settings updated",
          type: "success",
        });
      },
    });
  });

  selector.selectpicker("refresh");
  $("#diallerInboundCallSettingsDiv").removeClass("d-none");
};

// END - DIALLER INBOUND CALL ROUTING //

// DIALLER SPOOFING AND CYCLING //
const hasDialler = teamHasDialler();
let verifiedNumber;
const assignedNumbers = {
  AT: {},
  AU: {},
  CA: {},
  CZ: {},
  DE: {},
  FI: {},
  GB: {},
  NL: {},
  PT: {},
  RO: {},
  US: {},
};
const ADVANCED_SEARCH_COUNTRIES = ["AT", "CA", "CZ", "DE", "RO", "US"];
let complianceApproved = {};

const protectedAssignedNumber = (phone) => {
  const now = Date.now();
  const MIN_PHONE_AGE = 31 * 24 * 60 * 60;
  return phone.createdAt + MIN_PHONE_AGE > now / 1000;
};

const disabledAssignedNumber = (countryCode, disabled = true) => {
  const assigned = assignedNumbers[countryCode] || {};
  if (Object.keys(assigned).length === 0) return;

  ["country", "phone"].forEach((k) => {
    $(`.${countryCode}-assignedNumber-${k}`).toggleClass(
      "disabled-dialler-number",
      disabled,
    );
  });
  const disableBtn = protectedAssignedNumber(assigned) || disabled;
  $(`.${countryCode}-assignedNumber-remove-btn`)
    .prop("disabled", disableBtn)
    .toggleClass("disabled", disableBtn);

  $(".assigned-number-override").remove();
  if (!disabled) return;
  const tooltip = wrapTooltip(
    `<i class="assigned-number-override fas fa-info-circle ml-4 text-gray-500"></i>`,
    "This assigned number will not be used.<br>A verified number is set up for this region.",
    "top",
  );
  $(`#${countryCode}-assignedNumber`).append(tooltip);
};

const diallerRemoveNumberWarning = (number, countryCode) => {
  const msg = `Are you sure you want to remove <strong>${number}</strong>? <br><br>Calls made to it will not be forwarded to you, and this action cannot be undone.`;
  $(".removeNumberWarning").html(msg);
  $("#diallerRemoveNumberModal").modal("show");

  $("#removeNumberCancelBtn")
    .off("click")
    .on("click", () => {
      $("#diallerRemoveNumberModal").modal("hide");
    });

  $("#removeNumberConfirmBtn")
    .off("click")
    .on("click", () => {
      // Modal gets closed by "deleteDiallerNumber" websocket message
      addSpinner("#removeNumberConfirmBtn");
      wrappedPost({
        url: "/api/dialler/modify_numbers",
        data: JSON.stringify({ action: "delete", countryCode }),
      });
    });
};

const fillVerifiedCaller = (verified) => {
  verifiedNumber = verified;
  $("#diallerVerifiedCaller").empty();

  const phoneCallingCode = $("#callingCodeSelect").selectpicker("val");
  const phone = $("#phoneInput").val();

  const COUNTRY_MAP = {
    "+1": getUser().country === "Canada" ? "Canada" : "United States",
    "+31": "Netherlands",
    "+351": "Portugal",
    "+40": "Romania",
    "+43": "Austria",
    "+44": "United Kingdom",
    "+49": "Germany",
    "+61": "Australia",
    "+358": "Finland",
    "+420": "Czech Republic",
  };
  const country = COUNTRY_MAP[phoneCallingCode] || "";

  const FLAG_MAP = {
    Austria: "🇦🇹",
    Australia: "🇦🇺",
    Canada: "🇨🇦",
    "Czech Republic": "🇨🇿",
    Finland: "🇫🇮",
    Germany: "🇩🇪",
    Netherlands: "🇳🇱",
    Portugal: "🇵🇹",
    Romania: "🇷🇴",
    "United Kingdom": "🇬🇧",
    "United States": "🇺🇸",
  };

  const callingCodes =
    getUser().teamId === "rebel-recruiting-ug" ? ["+49"] : ["+1", "+44", "+61"];

  const MAPPINGS = {
    remove: {
      btnTitle: "Remove",
      btnTooltip: "Click to remove your verified number",
      btnId: "deleteVerifiedCallerBtn",
      btnClass: "dialler-danger-btn",
      inputClasses: "",
      number: (verified || {}).number,
      flag: (verified || {}).flag,
      country: (verified || {}).country,
      visible: true,
    },
    add: {
      btnTitle: "Verify",
      btnTooltip: "Click to verify your number",
      btnId: "addVerifiedCallerBtn",
      btnClass: "dialler-success-btn",
      inputClasses: "text-gray-500",
      number: `${phoneCallingCode}${phone}`,
      flag: FLAG_MAP[country] || "",
      country,
      visible: !!phone && callingCodes.includes(phoneCallingCode),
    },
  };

  const isVerified = ![undefined, null].includes(verified);
  const mapper = MAPPINGS[isVerified ? "remove" : "add"];

  $("#diallerVerifiedCallerContainer").toggle(mapper.visible);

  if (verified) disabledAssignedNumber(verified.countryCode, true);
  if (!mapper.visible) return;

  const { btnTooltip, btnId, btnTitle, inputClasses } = mapper;

  const loc = `<div class="diallerVerifiedCallerCountry form-control ${inputClasses}" style="min-width: 200px; padding: 0.75rem;"><i class="mr-3">${mapper.flag}</i> ${mapper.country}</div>`;
  const wrappedLoc = wrapTooltip(
    loc,
    `Your number ${verified ? "is" : "will be"} verified for calls to:<br/>${
      mapper.country
    }`,
    "bottom",
  );

  $("#diallerVerifiedCaller").append(`
      <div class="d-flex justify-content-start" style="align-items: center;">
        ${wrappedLoc}
        <form>
          <input id="verifiedCallerInput" disabled class="form-control ml-2 ${inputClasses}" type="text"/>
        </form>
        ${wrapTooltip(
          `<button id="${btnId}" class="ml-4 btn dialler-verify-btn ${mapper.btnClass}" type="button">${btnTitle}</button>`,
          btnTooltip,
          "top",
        )}
      </div>
    `);

  $("#verifiedCallerInput").val(mapper.number);
};

const diallerRemoveCallerIdWarning = () => {
  const { countryCode, number } = verifiedNumber;
  let msg = `Are you sure you want to remove your caller ID <strong>(${number})</strong>?`;
  if (assignedNumbers[countryCode].phone) {
    msg += ` <br><br>Once removed, calls made in this region will revert back to your assigned number <strong>(${assignedNumbers[countryCode].phone})</strong>.`;
  }
  $(".removeCallerIdWarning").html(msg);
  $("#diallerRemoveCallerIdModal").modal("show");

  $("#removeCallerIdCancelBtn")
    .off("click")
    .on("click", () => {
      $("#diallerRemoveCallerIdModal").modal("hide");
    });

  $("#removeCallerIdConfirmBtn")
    .off("click")
    .on("click", () => {
      addSpinner("#removeCallerIdConfirmBtn");
      wrappedPost({
        url: "/api/dialler/modify_verified_caller",
        data: JSON.stringify({ action: "delete" }),
        success: () => {
          addSpinner("#removeCallerIdConfirmBtn");
          $("#diallerRemoveCallerIdModal").modal("hide");
          disabledAssignedNumber(countryCode, false);
          fillVerifiedCaller(undefined);
          showToast({
            title: "Verified Caller ID removed",
            type: "success",
          });
        },
      });
    });
};

const addDiallerPhoneHtml = (p) => {
  const protect = protectedAssignedNumber(p);
  p.protect = protect;
  assignedNumbers[p.countryCode] = p;

  $("#addNewAssignedNumberBtn").tooltip("dispose");
  if (
    userDiallerCountries().every((c) => assignedNumbers[c]?.protect === true)
  ) {
    $("#addNewAssignedNumberBtn").prop("disabled", true);
    $("#addNewAssignedNumberBtn").tooltip({
      title:
        "You can only add new phone numbers once a number has been active for more than 1 month",
    });
  }

  const title = `This number is assigned for calls to:<br>${
    p.country || p.countryCode
  }`;

  const id = `${p.countryCode}-assignedNumber`;
  const flagDiv = `<div class="${id}-country form-control" style="min-width: 200px; padding: 0.75rem;" id="diallerAssignedNumberCountry"><span class="mr-2">${p.flag}</span>${p.country}</div>`;
  const removeBtn = `<button ${protect ? "disabled" : ""} data-phone-cc="${
    p.countryCode
  }" data-phone-number=${
    p.phone
  } class="remove-number ${id}-remove-btn ml-4 btn dialler-danger-btn">Remove</button>`;

  const html = `
    <div id="${id}" class="assigned-number-div mb-2 d-flex justify-content-start" style="align-items: center;">
      ${wrapTooltip(flagDiv, title, "top")}
      <form>
        <input disabled class="${id}-phone form-control ml-2" type="text" value=${
          p.phone
        } />
      </form>
      ${wrapTooltip(
        removeBtn,
        protect
          ? "You can only remove a number once it has been<br>active for more than 1 month"
          : "Removing a number will permanently remove the number from your account",
        "top",
      )}
    </div>
    `;

  $(`#diallerAssignedNumbers`).append(html);
};

// END DIALLER SPOOFING AND CYCLING //

// DIALLER - SEARCH NUMBERS //

const toggleAreaCodes = (show) => {
  ADVANCED_SEARCH_COUNTRIES.forEach((cc) => {
    $(`.areaCodeSelector[data-countrycode=${cc}`).selectpicker("val", "");
  });
  $(".areaCodeSelectorDiv").addClass("d-none");
  if (show) {
    const cc = $("#searchNumberCountryCodeSelect").selectpicker("val");
    $(`.areaCodeSelectorDiv[data-countrycode=${cc}]`).removeClass("d-none");
  }
};

const toggleAdvancedSearch = (show) => {
  $("#advancedSearchSelector").toggleClass("expanded", show);
  $("#searchNumberAdvanced").toggleClass("d-none", !show);
};

const clearSearchResults = () => {
  $("#diallerSearchResults").empty().removeClass("bordered");
  $("#addDiallerNumberBtnWrapper").addClass("d-none");
  $("#addDiallerNumberWarning").addClass("d-none");
};

const initAssignedNumberModal = () => {
  clearSearchResults();
  $("#disabledSearchWarning").toggleClass("d-none", true);
  $("#diallerSearchNumbersModal").modal("show");
};

// END DIALLER SEARCH NUMBERS //

// DIALLER CUSTOM VOICEMAILS //

const conditionalPauseAudio = (kind) => {
  const audioEl = document.getElementById(`new-${kind}-audio`);
  if (audioEl.paused) return;
  $(`.play-pause-audio-btn[data-id="new-${kind}"]`).click();
};

const cancelRecording = (kind) => {
  $(".new-recording-btn").removeClass("disabled");
  $(`#new-${kind}-audio`).removeAttr("src");
  $(`#add-new-${kind}-btn`).removeClass("d-none");
  $(`#create-new-${kind}-controls`).toggleClass("d-flex d-none");
  conditionalPauseAudio(kind);
};

const voicemailAudioEventHandlers = (id) => {
  $(`#${id}-audio`)
    .off("loadedmetadata")
    .on("loadedmetadata", (e) => {
      const duration = timestampToDuration(e.currentTarget.duration);
      $(`#${id}-duration-text`).text(`00:00 / ${duration}`);

      $(`#${id}-audio`)
        .off("timeupdate")
        .on("timeupdate", (e_) => {
          const t = timestampToDuration(e_.currentTarget.currentTime);
          $(`#${id}-duration-text`).text(`${t} / ${duration}`);
        });
    });

  $(`#${id}-audio`)
    .off("ended")
    .on("ended", () => {
      $(`.play-pause-audio-icon[data-id="${id}"]`)
        .removeClass("fa-circle-pause")
        .addClass("fa-circle-play");
    });
};

const openCreateRecordingRow = (kind) => {
  $(".new-recording-btn").addClass("disabled");
  $(`#add-new-${kind}-btn`).addClass("d-none");
  $(`#new-${kind}-name`).val("");
  $(`#new-${kind}-audio`).removeAttr("src");
  $(`#create-new-${kind}-controls`).toggleClass("d-none d-flex");
  $(`#new-${kind}-playback`).removeClass("d-flex").addClass("d-none");
  $(`.${kind}-record-btn-div`).removeClass("d-none").addClass("d-flex");
  $(`#new-${kind}-duration`).text("");
};

const addVoicemailRecordingRow = (obj, refreshSelect = false) => {
  const existing = $(`.recording-row[data-id="${obj.voicemailId}"]`);
  if (existing.length > 0) existing.remove();

  const { kind, name, url, voicemailId } = obj;
  const html = `
      <div class="d-flex recording-row" style="gap: 20px;" data-id="${voicemailId}">
        <div style="padding-top: 1rem; background-color: transparent; flex-direction: column; gap: 0.5rem;" class="button d-flex align-items-center play-pause-audio-btn" data-id="${voicemailId}">
          <i style="padding: 0.25rem 1.4rem;" class="play-pause-audio-icon text-primary fa-xl fa-solid fa-circle-play" data-id="${voicemailId}"></i>
          <div
            class="d-flex justify-content-center text-gray-500"
            style="font-size: 0.7rem;"
            id="${voicemailId}-duration-text"
          ></div>
        </div>
        <audio id="${voicemailId}-audio"  data-id="${voicemailId}" class="voicemail-audio" src="${url}" type="audio/wav"></audio>
        <div style="width: 100%;" class="d-flex align-items-center">
          <input data-kind="${kind}" class="form-control recording-name-input" data-id="${voicemailId}" value="${name}"></input>
        </div>
        <div class="d-flex align-items-center">
        <button class="btn dialler-danger-btn delete-recording-btn" data-id="${voicemailId}" type="button">Remove</button>
        </div>
      </div>`;
  const parent = `voicemail${kind === "greeting" ? "Greetings" : "Messages"}`;
  $(`#${parent}`).append(html);
  voicemailAudioEventHandlers(voicemailId);

  if (kind === "greeting") {
    const selector = $("#default-greeting-selector");
    selector.append(`<option value="${voicemailId}">${name}</option>`);
    if (refreshSelect) selector.selectpicker("refresh");
  }
};

const saveRecording = async (kind) => {
  const audioEl = document.getElementById(`new-${kind}-audio`);
  const { src } = audioEl;

  if (!audioEl.hasAttribute("src") || ["", undefined].includes(src)) {
    showToast({
      title: "Please create a recording before saving",
      type: "warning",
    });
    return;
  }

  const name = $(`#new-${kind}-name`).val();
  if ([undefined, null, ""].includes(name)) {
    showToast({
      title: "Please enter a name for the recording",
      type: "warning",
    });
    return;
  }

  wrappedPost({
    url: "/api/dialler/voicemails",
    data: JSON.stringify({
      action: "save",
      data: { kind, name, url: src },
    }),
    success: () => {
      conditionalPauseAudio(kind);
      $(".new-recording-btn").removeClass("disabled");
      $(`#add-new-${kind}-btn`).removeClass("d-none");
      $(`#create-new-${kind}-controls`).toggleClass("d-flex d-none");
      showToast({
        title:
          "Success! Your recording is being processed and will be available shortly.",
        type: "success",
      });
    },
  });
};

const recordDeviceAudio = (kind) => {
  const audioEl = document.getElementById(`new-${kind}-audio`);
  const recordedChunks = [];
  const constraints = { audio: true };

  return navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (e) => {
        recordedChunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: "audio/webm" });
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const audioDataURL = reader.result;
          audioEl.src = audioDataURL;
        };
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      return mediaRecorder;
    })
    .catch((err) => {
      console.error("Error accessing microphone:", err);
    });
};

let isRecording = false;
let recorder;
let recordingDurationInterval;

const handleDefaultGreetingChange = (e) => {
  const voicemailId = $(e.target).selectpicker("val");
  wrappedPost({
    url: "/api/dialler/voicemails",
    data: JSON.stringify({ action: "default-greeting", voicemailId }),
    success: () => {
      showToast({
        title: "Default Voicemail greeting saved",
        type: "success",
      });
    },
  });
};

// END - DIALLER CUSTOM VOICEMAILS //

const updateWarmUpMessage = (start, perDay, daysLeft, adminLimit) => {
  const increment = parseInt(perDay, 10);
  let days = parseInt(daysLeft, 10);
  let limit = parseInt(start, 10) + increment * days;

  if (adminLimit) {
    // Recalculate the limit and days to reach it based on the admin limit
    limit = Math.min(limit, adminLimit);
    const daysToReachLimit = Math.ceil((adminLimit - start) / increment);
    days = Math.min(days, daysToReachLimit);
  }

  const text =
    increment === 0 || days === 0
      ? `Your daily sending limit will remain at ${limit}`
      : `Your daily sending limit will reach ${limit} in ${days} days` +
        (adminLimit ? ` (Admin limit: ${adminLimit})` : "");
  $("#warmUpInfoGroup").show();
  $("#warmUpInfoText").text(text);
};

const fillProfile = () => {
  wrappedGet({
    url: "/api/users/get",
    data: { userId: aliasSelector.selectpicker("val"), fullProfile: true },
    success: (res) => {
      aliasSelector.empty();

      $("#profileImg").attr("src", res.picture);

      if (Object.keys(res.aliasDict).length > 1) {
        $("#aliasSelectorContainer").show();
        $("#nameParagraph").text(res.name);
        $("#emailParagraph").hide();

        Object.entries(res.aliasDict)
          .reverse()
          .forEach(([key, value]) => {
            aliasSelector.append(
              $("<option>", { value: key, text: value.email }),
            );
          });

        aliasSelector.val(res.userId);
        aliasSelector.selectpicker("refresh");
      } else {
        $("#emailParagraph").text(res.email);
        $("#nameParagraph").text(res.name);
      }

      if (!res.email.endsWith("@gmail.com")) {
        $("#sendLimitInput").prop("max", 1000);
      }

      customHolidays = res.customHolidays;
      displayCustomHolidays();
      $("#holidayCountriesSelector").selectpicker("val", res.avoidCountries);

      $("#timezoneSelector").selectpicker("val", res.timezone);

      $("#sendLimitInput").val(res.sendLimit);
      $("#minGapInput").val(res.minGap);

      if (res.emailSendSettings) {
        if (res.emailSendSettings.sendLimitLocked === true) {
          $("#sendLimitInput").prop("disabled", true);
          $("#sendLimitOutput").prop("disabled", true);
          $("#sendLimitInput").val(res.emailSendSettings.sendLimit);
          $("#sendLimitInputLocked .locked-value").text(
            res.emailSendSettings.sendLimit,
          );
          $("#sendLimitInputLocked").toggle(true);
        }

        if (res.emailSendSettings.minGapLocked === true) {
          $("#minGapInput").prop("disabled", true);
          $("#minGapInput").val(res.emailSendSettings.minGap);
          $("#minGapInputLocked .locked-value").text(
            `${res.emailSendSettings.minGap}s`,
          );
          $("#minGapInputLocked").toggle(true);
        }

        $("#sendLimitInput").prop(
          "max",
          res.emailSendSettings.sendLimit || 1000,
        );
        $("#minGapInput").prop("min", res.emailSendSettings.minGap || 5);
      }

      $("#archiveOldCampaignsCheckbox").prop(
        "checked",
        res.archiveOldCampaigns,
      );
      $("#archiveBouncesCheckbox").prop("checked", res.archiveBounces);
      $("#archiveAutoRepliesCheckbox").prop("checked", res.archiveAutoReplies);
      // Handle "undefined" OOO flag as true until all users backfilled
      $("#pauseCampaignsOnOutOfOffice").prop(
        "checked",
        res.pauseCampaignsOnOutOfOffice ?? true,
      );
      $("#dailyTodoNotificationsCheckbox").prop(
        "checked",
        res.dailyTodoNotifications,
      );

      $("#sendMonthlyCoachingEmailsCheckbox").prop(
        "checked",
        res.sendMonthlyCoachingEmails,
      );
      $("#approveEmailsCheckbox").prop("checked", res.approveEmails);
      $("#approveSmsCheckbox").prop("checked", res.approveSms);

      // Inbox Agent settings
      $("#inboxAgentDraftAIEnabledToggle").prop(
        "checked",
        res.inboxAgentDraftAIEnabled,
      );

      const sigEditor = tinymce.get("sigEditor");
      if (sigEditor) sigEditor.setContent(res.signature);
      else
        createEditor({
          selector: "#sigEditor",
          content: res.signature,
          options: { code: true, customButton: true },
        });

      $("#addSignatureToRepliesCheckbox").prop(
        "checked",
        res.addSignatureToReplies,
      );
      $("#includeUnsubscribeCheckbox").prop("checked", res.includeUnsubscribe);
      $("#removeGmailSignatureLineCheckbox").prop(
        "checked",
        res.removeGmailSignatureLine,
      );
      $("#bccInput").tokenfield("setTokens", res.bcc);
      $("#calendarLinkInput").val(res.calendarLink);
      $("#callingCodeSelect").selectpicker("val", res.phoneCallingCode);
      $("#phoneInput").val(res.phone);

      refreshLimitInput();
      refreshGapInput();

      if (
        res?.warmUpDaysLeft > 0 &&
        res?.warmUpPerDay > 0 &&
        !res?.emailSendSettings?.sendLimitLocked
      ) {
        $("#warmUpPerDayInput, #warmUpPerDayOutput").val(res.warmUpPerDay);
        $("#warmUpDaysLeftInput, #warmUpDaysLeftOutput").val(
          res.warmUpDaysLeft,
        );
        $("#warmUpToggle").prop("checked", true);
        $("#warmUpPerDayGroup, #warmUpDaysLeftGroup, #warmUpInfoGroup").show();

        updateWarmUpMessage(
          res.sendLimit,
          res.warmUpPerDay,
          res.warmUpDaysLeft,
          res.emailSendSettings?.sendLimit || null,
        );
      } else {
        $(
          "#warmUpPerDayInput, #warmUpDaysLeftInput, #warmUpPerDayOutput, #warmUpDaysLeftOutput",
        ).val(0);
        $("#warmUpToggle").prop("checked", false);
      }

      if (res.enforceUnsubscribeLink) {
        $("#includeUnsubscribeCheckbox").prop("checked", true);
        $('label[for="includeUnsubscribeCheckbox"]').css("opacity", 0.5);

        if ($("#includeUnsubscribeWarning").length === 0) {
          $('label[for="includeUnsubscribeCheckbox"]').after(
            '<i class="ml-2 fas fa-info-circle" data-toggle="tooltip" title="Your team has enforced this setting" id="includeUnsubscribeWarning"></i>',
          );

          $("#includeUnsubscribeWarning")
            .tooltip("dispose")
            .tooltip({ html: true, target: "hover" });
        }
      }

      if (hasDialler && hasConversationsLicense(res)) {
        const licenseMapping = {
          AU: "ausConversations",
          CA: "usConversations",
          GB: "ukConversations",
          US: "usConversations",
        };
        EU_DIALLER_COUNTRIES.forEach((country) => {
          licenseMapping[country] = "euConversations";
        });
        const countries = userDiallerCountries().filter((country) =>
          hasLicense(res, licenseMapping[country]),
        );

        const searchSelector = $("#searchNumberCountryCodeSelect");

        searchSelector.find("option").prop("disabled", true);
        countries.forEach((c) => {
          searchSelector.find(`option[value=${c}]`).prop("disabled", false);
        });
        searchSelector.selectpicker("refresh");

        ["AU", "KYC", ...EU_DIALLER_COUNTRIES].forEach((region) => {
          const lowerRegion = region.toLowerCase();
          const prefix =
            lowerRegion === "kyc" ? lowerRegion : `${lowerRegion}Compliance`;
          complianceApproved[lowerRegion] = res[`${prefix}Approved`];
        });

        $("#diallerAssignedNumbers").empty();
        if ((res.userPhones || []).length > 0) {
          res.userPhones.forEach((p) => {
            addDiallerPhoneHtml(p);
          });
        }
        fillVerifiedCaller(res.diallerVerifiedCaller);

        showInboundCallDevices(res.mobileAppDevice);

        $("#voicemailGreetings").empty();
        $("#voicemailMessages").empty();
        $("#default-greeting-text").html(
          `Your SourceWhale default greeting is:<br/><i>"Hi. You've reached the voicemail of ${res.name}. Please leave a message after the tone.</i>"`,
        );

        const greetingSelector = $("#default-greeting-selector");
        greetingSelector.find("option").remove();
        greetingSelector.append(
          `<option value="default">--- SourceWhale Default ---</option>`,
        );
        let defaultGreeting = "default";
        (res.diallerVoicemails || []).forEach((obj) => {
          if (obj.isDefault) defaultGreeting = obj.voicemailId;
          addVoicemailRecordingRow(obj, false);
        });
        greetingSelector
          .selectpicker("refresh")
          .selectpicker("val", defaultGreeting);

        greetingSelector.on("change", (e) => {
          handleDefaultGreetingChange(e);
        });

        $("#diallerSettingsCard").show();
      }

      if (userHasWhatsapp() && hasConversationsLicense(res)) {
        const status = getUser().whatsappAccountStatus;
        if (status === "active") {
          $("#whatsappStatusText").text("Your WhatsApp account is connected!");
        } else if (status === "disconnected") {
          $("#whatsappStatusText").text(
            "There is an issue with your WhatsApp integration. Please reconnect.",
          );
        }

        if (["disconnected", "deleted", "notConnected"].includes(status)) {
          $("#whatsappDisclaimerCheckbox").change((e) => {
            $("#connectWhatsappBtn").prop("disabled", !e.target.checked);
          });

          $("#connectWhatsappBtn")
            .off("click")
            .on("click", () => {
              const disclaimerConfirmed = $("#whatsappDisclaimerCheckbox").prop(
                "checked",
              );
              wrappedGet({
                url: "/api/whatsapp/connect",
                data: { disclaimerConfirmed },
                success: (url) => {
                  $("#whatsappDisclaimerModal").modal("hide");
                  window.open(url, "_blank");
                },
                spinner: "#connectWhatsappBtn",
              });
            });

          $("#openWhatsappDisclaimerBtn")
            .off("click")
            .on("click", () => {
              $("#connectWhatsappBtn").show();
              $("#whatsappDisclaimerModal").modal("show");
            })
            .show();
        }

        $("#whatsappSettingsCard").show();
      }
    },
  });
};

const removeCustomHoliday = (text) => {
  customHolidays = customHolidays.filter((x) => x !== text);
  displayCustomHolidays();
};

const addCustomHoliday = (text) => {
  if (customHolidays.includes(text)) return;
  customHolidays.push(text);
  displayCustomHolidays();
};

const phoneModules = hasDialler ? "SMS or dialer" : "SMS";
const phoneTooltip = `This number will be used when using the "senderPhone" variable in campaigns. Callbacks in response to your ${phoneModules} activity will be redirected to this number.`;
const tooltip = wrapTooltip(
  `<button id="phoneInputTooltip" class="btn btn-light" style="height: 100%;"><i class="fas fa-info-circle"></i></button>`,
  phoneTooltip,
  "top",
);

const appendTimezoneOffsetsToDropdown = () => {
  $("#timezoneSelector option").each((_, element) => {
    const tz = $(element).val();
    if (tz) {
      const offset = moment.tz(tz).format("Z");
      const formattedOffset = `GMT${offset}`;
      $(element).text(`${$(element).text()} (${formattedOffset})`);
    }
  });
};

export const initializeProfile = () => {
  window.onbeforeunload = () =>
    timeSettingsAreDirty ||
    contentSettingsAreDirty ||
    outreachSettingsAreDirty ||
    emailSettingsAreDirty ||
    inboxAgentSettingsAreDirty ||
    tinymceOnBeforeUnload();

  const user = getUser();
  // either the user has inbox agent (i.e. is modularized) or the user
  // is on a non-modularized team that we have grandfathered in the
  // draft AI feature.
  if (
    hasLicense(user, "inboxAgent") &&
    (user.licenses || user.inboxAgentDraftAI)
  ) {
    $("#inboxAgentCard").removeClass("d-none");
    $("#inboxAgentDraftAIEnabledToggle").prop(
      "checked",
      getUser().inboxAgentDraftAIEnabled,
    );
  }

  $("#timeSettingsCard input, #timeSettingsCard select").on("change", () => {
    timeSettingsAreDirty = true;
  });

  $("#contentSettingsCard input").on("change", () => {
    contentSettingsAreDirty = true;
  });

  $("#outreachSettingsCard input").on("change", () => {
    outreachSettingsAreDirty = true;
  });

  $("#emailSettingsCard input").on("change", () => {
    emailSettingsAreDirty = true;
  });

  $("#inboxAgentCard input").on("change", () => {
    inboxAgentSettingsAreDirty = true;
  });

  $("#diallerRemoveNumberModal").on("hidden.bs.modal", () => {
    $(".removeNumberWarning").html("");
    $("#diallerRemoveNumberModal").attr("data-phone-cc", "");
    $("#diallerRemoveNumberModal").modal("hide");
  });

  $(document).on("click", "#addVerifiedCallerBtn", () => {
    const data = JSON.stringify({
      code: $("#callingCodeSelect").selectpicker("val"),
      number: $("#phoneInput").val(),
      action: "verify",
    });
    addSpinner("#addVerifiedCallerBtn");
    wrappedPost({
      url: "/api/dialler/modify_verified_caller",
      data,
      success: (ret) => {
        if (ret.validationCode) {
          $("#diallerVerificationCode").text(ret.validationCode);
          $("#diallerVerifyCallerModal").modal("show");
        }
      },
    });
  });

  $(document).on("click", ".remove-number", (e) => {
    const el = $(e.currentTarget);
    const num = el.attr("data-phone-number");
    diallerRemoveNumberWarning(num, el.attr("data-phone-cc"));
  });

  $(document).on("click", "#deleteVerifiedCallerBtn", () => {
    const code = $("#callingCodeSelect").selectpicker("val");
    const number = $("#phoneInput").val();
    diallerRemoveCallerIdWarning(number, code);
  });

  $("#addNewAssignedNumberBtn").click(() => {
    initAssignedNumberModal();
  });

  $("#advancedSearchSelector").on("click", () => {
    const showing = $("#advancedSearchSelector").hasClass("expanded");
    toggleAdvancedSearch(!showing);
    toggleAreaCodes(true);
  });

  $("#searchNumberCountryCodeSelect").on("change", () => {
    clearSearchResults();
    const cc = $("#searchNumberCountryCodeSelect").selectpicker("val");
    $("#diallerSearchNumberDiv [id$=Warning]").toggleClass("d-none", true);

    if (cc === "GB" && !complianceApproved.kyc) {
      $("#searchNumberAdvancedDiv").toggleClass("d-none", true);
      $("#noKycWarning").toggleClass("d-none", false);
      $("#searchDiallerNumbersBtn").prop("disabled", true);
      return;
    }

    if (
      !["US", "CA", "GB", "NL"].includes(cc) &&
      !complianceApproved[cc.toLowerCase()]
    ) {
      const camelCaseCC = cc.charAt(0) + cc.slice(1).toLowerCase();
      $("#searchNumberAdvancedDiv").toggleClass("d-none", true);
      $(`#no${camelCaseCC}Warning`).toggleClass("d-none", false);
      $("#searchDiallerNumbersBtn").prop("disabled", true);
      return;
    }

    if (assignedNumbers[cc].protect) {
      $("#searchNumberAdvancedDiv").toggleClass("d-none", true);
      $("#disabledSearchWarning").toggleClass("d-none", false);
      $("#searchDiallerNumbersBtn").prop("disabled", true);
      return;
    }

    if (
      EU_DIALLER_COUNTRIES.includes(cc) &&
      EU_DIALLER_COUNTRIES.some((cc) => Object.keys(assignedNumbers[cc]).length)
    ) {
      $("#searchNumberAdvancedDiv").toggleClass("d-none", true);
      $("#euHomeCountryWarning").toggleClass("d-none", false);
      $("#searchDiallerNumbersBtn").prop("disabled", true);
      return;
    }

    if (["CZ", "RO"].includes(cc)) {
      $(`#smsNotSupportedWarning`).toggleClass("d-none", false);
      const regionText = cc === "CZ" ? "Czech" : "Romanian";
      $("#smsNotSupportedRegion").text(regionText);
    }

    $("#disabledSearchWarning").toggleClass("d-none", true);
    $("#searchNumberAdvancedDiv").toggleClass(
      "d-none",
      !ADVANCED_SEARCH_COUNTRIES.includes(cc),
    );
    toggleAdvancedSearch(false);
    toggleAreaCodes(false);
    $("#searchDiallerNumbersBtn").prop("disabled", false);
  });

  ["AT", "DE"].forEach((cc) => {
    $(`.areaCodeSelector[data-countrycode=${cc}]`).on("change", () => {
      const selectedValue = $(
        `.areaCodeSelector[data-countrycode=${cc}]`,
      ).selectpicker("val");
      if (selectedValue && selectedValue !== "") {
        $("#smsNotSupportedWarning").toggleClass("d-none", false);
        $("#smsNotSupportedRegion").text(
          cc === "AT" ? "local Austrian" : "local German",
        );
      } else {
        $("#smsNotSupportedWarning").toggleClass("d-none", true);
      }
    });
  });

  $("#searchDiallerNumbersBtn").click(() => {
    const cc = $("#searchNumberCountryCodeSelect").selectpicker("val");
    const areaCode = ADVANCED_SEARCH_COUNTRIES.includes(cc)
      ? $(`.areaCodeSelector[data-countrycode=${cc}]`).selectpicker("val")
      : undefined;

    const data = JSON.stringify({
      action: "search",
      countryCode: cc,
      areaCode,
    });

    addSpinner("#searchDiallerNumbersBtn");
    wrappedPost({
      url: "/api/dialler/modify_numbers",
      data,
      success: (res) => {
        removeSpinner("#searchDiallerNumbersBtn");
        $("#diallerSearchResults").empty();
        $("#diallerSearchResults").removeClass("bordered");
        const { phones } = res;
        if (phones.length === 0) {
          $("#diallerSearchResults").append(
            `<div class="d-flex justify-content-center mb-4 text-gray-500">No numbers available for this area. Please try a different area code</div>`,
          );
          $("#addDiallerNumberBtnWrapper").addClass("d-none");
          return;
        }

        phones.forEach((p) => {
          const { state, location, countryCode, kind, name, number } = p;
          const loc = [location, state].filter(Boolean).join(", ") || "--";

          $("#diallerSearchResults").append(
            `<div class="searchedNumberItem" data-kind="${kind}" data-countrycode="${countryCode}" data-phone="${number}">
                <input type="checkbox" class="checkbox-round selectedNumberCheckbox">
                <div class="searched-number-title">${name}</div>
                <div class="searched-number-subtext">${loc}</div>
              </div>`,
          );
          $("#diallerSearchResults").addClass("bordered");
        });
        $("#addDiallerNumberBtn").prop("disabled", true);
        $("#addDiallerNumberBtnWrapper").removeClass("d-none");
        if (Object.keys(assignedNumbers[cc]).length > 0) {
          $("#addDiallerNumberWarning").removeClass("d-none");
        }
      },
    });
  });

  $(document).on("click", ".searchedNumberItem", (e) => {
    const target = $(e.currentTarget);
    target.addClass("selected").siblings().removeClass("selected");
    $("#addDiallerNumberBtn").prop("disabled", false);
    $(".selectedNumberCheckbox").prop("checked", false);
    target.find(".selectedNumberCheckbox").prop("checked", true);
  });

  $("#addDiallerNumberBtn").click(() => {
    const selected = $(".searchedNumberItem.selected");
    const countryCode = selected.attr("data-countrycode");
    const kind = selected.attr("data-kind");
    const phone = selected.attr("data-phone");

    const data = JSON.stringify({
      action: "create",
      countryCode,
      kind,
      number: phone,
    });

    if (Object.keys(assignedNumbers[countryCode]).length > 0) {
      const msg = `Adding this number will delete your current assigned number for this region. Do you want to continue?`;
      yesNoConfirm(msg, () => {
        wrappedPost({
          url: "/api/dialler/modify_numbers",
          data,
          success: () => {},
          spinner: "#addDiallerNumberBtn",
        });
      });
    } else if (EU_DIALLER_COUNTRIES.includes(countryCode)) {
      const msg =
        "You can't change your home country number once it's selected";
      yesNoConfirm(msg, () => {
        wrappedPost({
          url: "/api/dialler/modify_numbers",
          data,
          success: () => {},
          spinner: "#addDiallerNumberBtn",
        });
      });
    } else {
      wrappedPost({
        url: "/api/dialler/modify_numbers",
        data,
        success: () => {},
        spinner: "#addDiallerNumberBtn",
      });
    }
  });

  ["greeting", "message"].forEach((kind) => {
    $(`#add-new-${kind}-btn`).click(() => {
      openCreateRecordingRow(kind);
    });

    $(`#new-${kind}-audio`)
      .off("ended")
      .on("ended", () => {
        $(`.play-pause-audio-icon[data-id="new-${kind}"]`)
          .removeClass("fa-circle-pause")
          .addClass("fa-circle-play");
      });

    $(`.cancel-${kind}-btn`).click(() => {
      cancelRecording(kind);
    });

    $(`.save-${kind}-btn`).click(() => {
      saveRecording(kind);
    });
  });

  $(document).on("change", ".recording-name-input", (e) => {
    const voicemailId = $(e.currentTarget).attr("data-id");
    const kind = $(e.currentTarget).attr("data-kind");
    wrappedPost({
      url: "/api/dialler/voicemails",
      data: JSON.stringify({
        action: "update",
        voicemailId,
        data: { name: $(e.currentTarget).val() },
      }),
      success: () => {
        if (kind === "greeting") {
          $(`#default-greeting-selector option[value="${voicemailId}"]`).text(
            $(e.currentTarget).val(),
          );
          $(`#default-greeting-selector`).selectpicker("refresh");
        }
        showToast({
          title: "Voicemail updated",
          type: "success",
        });
      },
    });
  });

  $(".record-btn").click((e) => {
    const kind = $(e.currentTarget).attr("data-kind");

    const startRecording = () => {
      recordDeviceAudio(kind).then((r) => {
        recorder = r;
        $(`#new-${kind}-playback`).removeClass("d-flex").addClass("d-none");
        isRecording = true;
        recordingDurationInterval = createDurationInterval(
          `new-${kind}-duration`,
        );
        $(`.record-btn[data-kind="${kind}"]`).addClass("active");
      });
    };

    const stopRecording = () => {
      if (!isRecording) return;
      clearInterval(recordingDurationInterval);
      recorder.stop();
      $(`.record-btn[data-kind="${kind}"]`).removeClass("active");
      isRecording = false;
      $(`#new-${kind}-playback`).toggleClass("d-none d-flex");
      $(`.${kind}-record-btn-div`).toggleClass("d-flex d-none");
    };

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
      setTimeout(stopRecording, kind === "greeting" ? 30000 : 60000);
    }
  });

  $(document).on("click", ".play-pause-audio-btn", (e) => {
    const id = $(e.currentTarget).attr("data-id");
    const audioEl = document.querySelector(`.voicemail-audio[data-id="${id}"]`);
    const action = audioEl.paused ? "play" : "pause";

    const playPauseAudio = (el, childAction) => {
      const add = childAction === "play" ? "fa-circle-pause" : "fa-circle-play";
      const remove =
        childAction === "play" ? "fa-circle-play" : "fa-circle-pause";
      const voicemailId = el.getAttribute("data-id");

      if (childAction === "pause") {
        el.pause();
      } else {
        el.play();
      }

      $(`.play-pause-audio-icon[data-id="${voicemailId}"]`)
        .removeClass(remove)
        .addClass(add);
    };

    const elements = document.querySelectorAll(".voicemail-audio");
    elements.forEach((el) => {
      if (!el.paused) playPauseAudio(el, "pause");
    });

    if (action === "play") {
      playPauseAudio(audioEl, "play");
    }
  });

  $(document).on("click", ".delete-recording-btn", (e) => {
    const voicemailId = $(e.currentTarget).attr("data-id");
    wrappedPost({
      url: "/api/dialler/voicemails",
      data: JSON.stringify({
        action: "delete",
        voicemailId,
      }),
      success: () => {
        $(`.recording-row[data-id="${voicemailId}"]`).remove();
        const selector = $("#default-greeting-selector");
        const opt = selector.find(`option[value="${voicemailId}"]`);
        if (opt.is(":selected")) selector.selectpicker("val", "default");
        opt.remove();
        selector.selectpicker("refresh");

        showToast({
          title: "Voicemail deleted",
          type: "success",
        });
      },
    });
  });

  fillProfile();

  aliasSelector.change(() => {
    fillProfile();
  });

  const warmUpSliderSpinner = (inputStr) => {
    const input = `${inputStr}Input`;
    const output = `${inputStr}Output`;

    const update = (tooltipPresent) => {
      if (inputStr === "#sendLimit" && tooltipPresent) {
        refreshLimitInput();
        $(input).tooltip("show");
      }
      if ($("#warmUpToggle").is(":checked")) {
        const adminLimit = getUser().emailSendSettings?.sendLimit || null;
        const start = $("#sendLimitOutput").val();
        const perDay = $("#warmUpPerDayOutput").val();
        const daysLeft = $("#warmUpDaysLeftOutput").val();
        updateWarmUpMessage(start, perDay, daysLeft, adminLimit);
      }
    };

    update(false);

    $(input)
      .off("input change")
      .on("input change", () => {
        $(output).val($(input).val());
        update(true);
      });

    $(output)
      .off("input")
      .on("input", (e) => {
        let value = e.target.value.replace(/[^0-9]/g, "");
        value = Math.min(
          Math.max(value, $(input).attr("min")),
          $(input).attr("max"),
        );
        $(input).val(value);
        $(output).val(value);
        update(true);
      });

    if (inputStr === "#sendLimit") {
      $(`${input}, ${output}`)
        .off("focus blur")
        .on("focus blur", (e) => {
          $(input).tooltip(e.type === "focus" ? "show" : "hide");
        });
    }
  };

  const updateWarmUp = () => {
    warmUpSliderSpinner("#sendLimit");
    warmUpSliderSpinner("#warmUpPerDay");
    warmUpSliderSpinner("#warmUpDaysLeft");
  };

  updateWarmUp();

  $(
    "#sendLimitInput, #warmUpPerDayInput, #warmUpDaysLeftInput, #warmUpToggle",
  ).change(() => {
    const checked = $("#warmUpToggle").is(":checked");
    if (
      checked &&
      getUser().emailSendSettings?.emailDailySendingLimitLocked === true
    ) {
      showToast({
        title: "Warm up feature is blocked",
        subtitle: "Your admin has locked your team's sending limits",
        type: "warning",
      });
      $("#warmUpToggle").prop("checked", false);
      return;
    }

    if (checked) {
      $("#warmUpPerDayGroup, #warmUpDaysLeftGroup, #warmUpInfoGroup").show();
      updateWarmUp();
    } else
      $("#warmUpPerDayGroup, #warmUpDaysLeftGroup, #warmUpInfoGroup").hide();
  });

  $("#minGapInput").on("input", (e) => {
    $("#minGapOutput").val(`${$(e.target).val()}s`);
  });

  $("#minGapInput").change((e) => {
    refreshGapInput();
    $(e.target).tooltip("show");
  });

  $("#minGapInput").blur((e) => {
    $(e.target).tooltip("hide");
  });

  $("#minGapInput").focus((e) => {
    $(e.target).tooltip("show");
  });

  $("#profileImg").click(() => $("#imageInput").trigger("click"));

  $("#imageInput").change((e) => {
    const { files } = e.target;
    const file = files[0];
    const reader = new FileReader();

    $("#profileImgSpinner").show();
    $("#profileImg").hide();
    reader.onload = (ev) => {
      wrappedPost({
        url: "/api/users/save",
        data: JSON.stringify({
          userId: aliasSelector.selectpicker("val"),
          pictureData: ev.target.result,
        }),
        success: (url) => {
          $("#profileImg, #userImg").attr("src", url);
          $("#profileImgSpinner").hide();
          $("#profileImg").show();
        },
        error: () => {
          $("#profileImgSpinner").hide();
          $("#profileImg").show();
        },
      });
    };
    reader.readAsDataURL(file);
  });

  displayCustomHolidays = () => {
    $("#customHolidaysDiv").empty();

    customHolidays
      .sort((a, b) => new Date(a) - new Date(b))
      .forEach((text) => {
        const id = uuidV4();

        const html = `
          <span class="badge badge-primary d-inline-flex align-items-center m-1 p-1">
            <span class="name ml-1 mr-2">${text}</span>
            <i class="fas fa-times" id="${id}-remove" style="cursor: pointer"></i>
          </span>`;

        $("#customHolidaysDiv").append(html);
        $(`#${id}-remove`).click(() => removeCustomHoliday(text));
        $("#customHolidayInput").val("");
      });
  };

  $("#customHolidayInput input").on("apply.daterangepicker", () => {
    addCustomHoliday(getStartDate("#customHolidayInput input"));
  });

  $("#saveAllChanges").click(() => {
    const settings = {
      userId: aliasSelector.selectpicker("val"),
      updates: {
        // Content
        signature: tinymce.get("sigEditor").getContent(),
        addSignatureToReplies: $("#addSignatureToRepliesCheckbox").is(
          ":checked",
        ),
        includeUnsubscribe: $("#includeUnsubscribeCheckbox").is(":checked"),
        removeGmailSignatureLine: $("#removeGmailSignatureLineCheckbox").is(
          ":checked",
        ),
        bcc: $("#bccInput")
          .tokenfield("getTokens")
          .map((x) => x.value),
        calendarLink: $("#calendarLinkInput").val(),
        phone: $("#phoneInput").val(),
        phoneCallingCode: $("#callingCodeSelect").selectpicker("val"),

        // Outreach
        sendLimit: $("#sendLimitInput").val(),
        minGap: $("#minGapInput").val(),
        warmUp: $("#warmUpToggle").is(":checked"),
        warmUpPerDay: $("#warmUpToggle").is(":checked")
          ? $("#warmUpPerDayInput").val()
          : 0,
        warmUpDaysLeft: $("#warmUpToggle").is(":checked")
          ? $("#warmUpDaysLeftInput").val()
          : 0,
        archiveOldCampaigns: $("#archiveOldCampaignsCheckbox").is(":checked"),
        archiveBounces: $("#archiveBouncesCheckbox").is(":checked"),
        archiveAutoReplies: $("#archiveAutoRepliesCheckbox").is(":checked"),
        dailyTodoNotifications: $("#dailyTodoNotificationsCheckbox").is(
          ":checked",
        ),
        sendMonthlyCoachingEmails: $("#sendMonthlyCoachingEmailsCheckbox").is(
          ":checked",
        ),
        approveEmails: $("#approveEmailsCheckbox").is(":checked"),
        approveSms: $("#approveSmsCheckbox").is(":checked"),
        pauseCampaignsOnOutOfOffice: $("#pauseCampaignsOnOutOfOffice").is(
          ":checked",
        ),
        // Time
        avoidCountries: $("#holidayCountriesSelector").selectpicker("val"),
        customHolidays,
        timezone: $("#timezoneSelector").selectpicker("val"),
        // Inbox Agent
        inboxAgentDraftAIEnabled: $("#inboxAgentDraftAIEnabledToggle").is(
          ":checked",
        ),
      },
    };
    wrappedPost({
      url: "/api/users/save",
      data: JSON.stringify(settings),
      success: () => {
        tinymce.editors.forEach((editor) => editor.setDirty(false));
        showToast({ title: "Profile settings saved", type: "success" });
        contentSettingsAreDirty = false;
        outreachSettingsAreDirty = false;
        timeSettingsAreDirty = false;
        emailSettingsAreDirty = false;
        inboxAgentSettingsAreDirty = false;

        if (hasDialler && !verifiedNumber) fillVerifiedCaller(undefined);
      },
      spinner: "#saveAllChanges",
    });
  });

  $("#bccInput").tokenfield({ createTokensOnBlur: true, inputType: "email" });

  $("#nameParagraph").keyup(() => {
    const userId = aliasSelector.selectpicker("val");
    const name = $("#nameParagraph").text();

    if (getUser().userId === userId) $("#userName").text(name);

    wrappedPost({
      url: "/api/users/save",
      data: JSON.stringify({ userId, updates: { name } }),
    });
  });

  $("#removeGmailSignatureLineDiv").toggle(getUser().provider === "gcp");

  $("#phoneInputTooltipContainer").empty().append(tooltip);

  appendTimezoneOffsetsToDropdown();

  addWebsocketListener((message) => {
    const { kind } = message;

    if (kind === "deleteDiallerNumber") {
      removeSpinner("#removeNumberConfirmBtn");
      $("#diallerRemoveNumberModal").modal("hide");

      const { phone } = message;
      assignedNumbers[phone.countryCode] = {};
      $(`#${phone.countryCode}-assignedNumber`).remove();
      showToast({
        title: `You have successfully removed your number (${phone.phone})`,
        type: "success",
      });
    }

    if (kind === "newDiallerNumber") {
      $("#diallerSearchNumbersModal").modal("hide");
      removeSpinner("#addDiallerNumberBtn");

      const { phone } = message;
      addDiallerPhoneHtml(phone);
      showToast({
        title: `You have successfully added a new number ${phone.phone}`,
        type: "success",
      });
    }

    if (kind === "createNumberError") {
      removeSpinner("#addDiallerNumberBtn");
      $(`.searchedNumberItem[data-phone="${message.number}"]`).remove();
    }

    if (kind === "verifiedCallerCreated") {
      const { status } = message;
      $("#diallerVerifyCallerModal").modal("hide");
      if (status === "failed") {
        showToast({
          title: "Error: Verification failed",
          subtitle: "Please try again or contact support if the issue persists",
          type: "danger",
        });
      }
      if (status === "success") {
        const verified = message.diallerVerifiedCaller;
        const prefix = verified.country === "Canada" ? "" : "the";
        const successMsg = `<span style="font-size: 0.9rem; opacity: 75%;" class="mb-4">Your number <strong>(${verified.number})</strong> has been verified and will now be used as the Caller ID for ${prefix} ${verified.country}.</span>`;
        $(".verificationMessage").html(successMsg);
        $("#diallerVerificationSuccessModal").modal("show");
        fillVerifiedCaller(verified);
        setTimeout(() => {
          $(".verificationMessage").empty();
          $("#diallerVerificationSuccessModal").modal("hide");
        }, 5000);
      }
      removeSpinner("#addVerifiedCallerBtn");
    }

    if (kind === "verifiedCallerDeleted") {
      showToast({
        title: "Your verified number has been removed",
        type: "warning",
      });
      disabledAssignedNumber(verifiedNumber.countryCode, false);
      fillVerifiedCaller(undefined);
    }

    if (kind === "updateDiallerVoicemails") {
      if (message.action === "save") {
        addVoicemailRecordingRow(message.item, true);
      }
    }
  });
};
